import {Component, computed, effect, ElementRef, inject} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {TranslatePipe} from '@ngx-translate/core';
import {map, of, switchMap, timer} from 'rxjs';
import {ConnectionStore, RESTORED_DISPLAY_MS} from 'src/app/services/ui/connection.store';
import {CountdownCloseButtonComponent} from '../../design-system/countdown-close-button/countdown-close-button.component';
import {IconTextButtonComponent} from '../../design-system/icon-text-button/icon-text-button.component';

/**
 * The banner at the top of the app for a global outage (KAB-2441): no network, or an engine that does not answer, until
 * the connection is back. Mounted once in the root component; a popover, so in the browser's top layer like the CDK
 * dialogs, raised again for each new state.
 */
@Component({
	selector: 'app-connection-banner',
	imports: [TranslatePipe, IconTextButtonComponent, CountdownCloseButtonComponent],
	templateUrl: './connection-banner.component.html',
	styleUrl: './connection-banner.component.scss',
	host: {popover: 'manual'},
})
export class ConnectionBannerComponent {
	protected readonly connection = inject(ConnectionStore);
	protected readonly restoredDuration = RESTORED_DISPLAY_MS;

	/** Seconds before the next probe, ticking only while one is scheduled. */
	protected readonly secondsLeft = toSignal(toObservable(this.connection.nextProbeAt).pipe(
		switchMap(at => at === null ? of(0) : timer(0, 1000).pipe(map(() => Math.max(0, Math.ceil((at - Date.now()) / 1000))))),
	), {initialValue: 0});
	protected readonly retryStatusKey = computed(() => this.connection.probing() ? 'global.connection.retrying' : 'global.connection.retryIn');

	constructor() {
		const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
		effect(() => {
			if (this.connection.issue()) {
				// Top layer entries paint in the order they were shown: above a dialog opened since.
				if (host.matches(':popover-open')) {
					host.hidePopover();
				}
				host.showPopover();
			}
		});
	}
}
